import { useState, useCallback } from 'react';

export interface TourStep {
  target: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="exceptions-header"]',
    title: 'Welcome to Audit Exceptions',
    content: 'This is your central hub for managing audit discrepancies and exceptions. Track critical issues, assign tasks, and ensure all exceptions are resolved.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="exceptions-metrics"]',
    title: 'Exception Metrics',
    content: 'Get a quick overview of exceptions by type, severity, and status. Click on any metric card to filter the list below.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="exceptions-view-mode"]',
    title: 'View Modes',
    content: 'Switch between List and Card views to display exceptions in your preferred format.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="exceptions-new-button"]',
    title: 'Create New Exception',
    content: 'Manually create a new exception when you discover discrepancies during audits.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="exceptions-list"]',
    title: 'Exceptions List',
    content: 'View all exceptions with details like severity, status, and assigned users. Click on any exception to view full details and take action.',
    placement: 'top',
  },
];

const STORAGE_KEY = 'exceptions-tour-completed';

export const useExceptionsTour = () => {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    }
    return false;
  });

  const startTour = useCallback(() => {
    setIsTourOpen(true);
    setCurrentStep(0);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
    setCurrentStep(0);
  }, []);

  const completeTour = useCallback(() => {
    setIsTourOpen(false);
    setCurrentStep(0);
    setHasCompletedTour(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  }, []);

  const resetTour = useCallback(() => {
    setHasCompletedTour(false);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  return {
    isTourOpen,
    tourSteps: TOUR_STEPS,
    currentStep,
    startTour,
    closeTour,
    completeTour,
    resetTour,
    nextStep,
    previousStep,
    hasCompletedTour,
  };
};
